var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getData, utils, v2, v3 } from "@tradetrust-tt/tradetrust";
import { getDocumentStoreRecords } from "@tradetrust-tt/dnsprove";
import { OpenAttestationDnsTxtCode } from "../../../types/error";
import { withCodedErrorHandler } from "../../../common/errorHandler";
import { CodedError } from "../../../common/error";
import { InvalidDnsTxtVerificationStatus, ValidDnsTxtVerificationStatus, ValidDnsTxtVerificationStatusArray, } from "./openAttestationDnsTxt.type";
const name = "OpenAttestationDnsTxtIdentityProof";
const type = "ISSUER_IDENTITY";
// Resolve identity of an issuer, currently supporting only DNS-TXT
// DNS-TXT is explained => https://github.com/Open-Attestation/adr/blob/master/decentralized_identity_proof_DNS-TXT.md
const resolveIssuerIdentity = (location, smartContractAddress, options) => __awaiter(void 0, void 0, void 0, function* () {
    const network = yield options.provider.getNetwork();
    const records = yield getDocumentStoreRecords(location);
    const matchingRecord = records.find((record) => record.addr.toLowerCase() === smartContractAddress.toLowerCase() &&
        record.netId === network.chainId.toString(10) &&
        record.type === "openatts" &&
        record.net === "ethereum");
    return matchingRecord
        ? {
            status: "VALID",
            location,
            value: smartContractAddress,
        }
        : {
            status: "INVALID",
            location,
            value: smartContractAddress,
            reason: {
                message: `Matching DNS record not found for ${smartContractAddress}`,
                code: OpenAttestationDnsTxtCode.MATCHING_RECORD_NOT_FOUND,
                codeString: OpenAttestationDnsTxtCode[OpenAttestationDnsTxtCode.MATCHING_RECORD_NOT_FOUND],
            },
        };
});
const skip = () => __awaiter(void 0, void 0, void 0, function* () {
    return {
        status: "SKIPPED",
        type,
        name,
        reason: {
            code: OpenAttestationDnsTxtCode.SKIPPED,
            codeString: OpenAttestationDnsTxtCode[OpenAttestationDnsTxtCode.SKIPPED],
            message: `Document issuers doesn't have "documentStore" / "tokenRegistry" property or doesn't use ${v3.IdentityProofType.DNSTxt} type`,
        },
    };
});
const test = (document) => {
    if (utils.isWrappedV2Document(document)) {
        const documentData = getData(document);
        // at least one issuer uses DNS-TXT
        return documentData.issuers.some((issuer) => {
            var _a;
            return ((issuer.documentStore || issuer.tokenRegistry || issuer.certificateStore) &&
                ((_a = issuer.identityProof) === null || _a === void 0 ? void 0 : _a.type) === v2.IdentityProofType.DNSTxt);
        });
    }
    else if (utils.isWrappedV3Document(document)) {
        return document.openAttestationMetadata.identityProof.type === v3.IdentityProofType.DNSTxt;
    }
    return false;
};
const verifyV2 = (document, options) => __awaiter(void 0, void 0, void 0, function* () {
    const documentData = getData(document);
    const identities = yield Promise.all(documentData.issuers.map((issuer) => {
        var _a;
        if (((_a = issuer.identityProof) === null || _a === void 0 ? void 0 : _a.type) === v2.IdentityProofType.DNSTxt) {
            const { location } = issuer.identityProof;
            const smartContractAddress = issuer.documentStore || issuer.tokenRegistry || issuer.certificateStore;
            if (!location)
                throw new CodedError("Location not found in identity proof", OpenAttestationDnsTxtCode.INVALID_ISSUERS, OpenAttestationDnsTxtCode[OpenAttestationDnsTxtCode.INVALID_ISSUERS]);
            if (!smartContractAddress)
                throw new CodedError("Smart contract address not found in identity proof", OpenAttestationDnsTxtCode.INVALID_ISSUERS, OpenAttestationDnsTxtCode[OpenAttestationDnsTxtCode.INVALID_ISSUERS]);
            return resolveIssuerIdentity(location, smartContractAddress, options);
        }
        const invalidResponse = {
            status: "INVALID",
            reason: {
                message: "Issuer is not using DNS-TXT identityProof type",
                code: OpenAttestationDnsTxtCode.INVALID_ISSUERS,
                codeString: OpenAttestationDnsTxtCode[OpenAttestationDnsTxtCode.INVALID_ISSUERS],
            },
        };
        return invalidResponse; // eslint is happy, so am I (https://github.com/bradzacher/eslint-plugin-typescript/blob/master/docs/rules/no-object-literal-type-assertion.md)
    }));
    if (ValidDnsTxtVerificationStatusArray.guard(identities)) {
        return {
            name,
            type,
            data: identities,
            status: "VALID",
        };
    }
    const invalidIdentity = identities.find(InvalidDnsTxtVerificationStatus.guard);
    if (InvalidDnsTxtVerificationStatus.guard(invalidIdentity)) {
        return {
            name,
            type,
            data: identities,
            reason: invalidIdentity.reason,
            status: "INVALID",
        };
    }
    throw new CodedError("Unable to retrieve the reason of the failure", OpenAttestationDnsTxtCode.UNEXPECTED_ERROR, "UNEXPECTED_ERROR");
});
const verifyV3 = (document, options) => __awaiter(void 0, void 0, void 0, function* () {
    if (document.openAttestationMetadata.proof.method !== v3.Method.DocumentStore &&
        document.openAttestationMetadata.proof.method !== v3.Method.TokenRegistry)
        throw new CodedError("DNS-TXT is only supported with documents issued using document store or token registry", OpenAttestationDnsTxtCode.UNSUPPORTED, OpenAttestationDnsTxtCode[OpenAttestationDnsTxtCode.UNSUPPORTED]);
    const smartContractAddress = document.openAttestationMetadata.proof.value;
    const { identifier } = document.openAttestationMetadata.identityProof;
    const issuerIdentity = yield resolveIssuerIdentity(identifier, smartContractAddress, options);
    if (ValidDnsTxtVerificationStatus.guard(issuerIdentity)) {
        return {
            name,
            type,
            data: {
                identifier: issuerIdentity.location,
                value: issuerIdentity.value,
            },
            status: "VALID",
        };
    }
    return {
        name,
        type,
        data: {
            identifier: issuerIdentity.location,
            value: issuerIdentity.value,
        },
        reason: issuerIdentity.reason,
        status: "INVALID",
    };
});
export const openAttestationDnsTxtIdentityProof = {
    skip,
    test,
    verify: withCodedErrorHandler((document, options) => __awaiter(void 0, void 0, void 0, function* () {
        if (utils.isWrappedV2Document(document))
            return verifyV2(document, options);
        else if (utils.isWrappedV3Document(document))
            return verifyV3(document, options);
        // this code is actually unreachable because of the test function
        throw new CodedError("Document does not match either v2 or v3 formats", OpenAttestationDnsTxtCode.UNRECOGNIZED_DOCUMENT, OpenAttestationDnsTxtCode[OpenAttestationDnsTxtCode.UNRECOGNIZED_DOCUMENT]);
    }), {
        name,
        type,
        unexpectedErrorCode: OpenAttestationDnsTxtCode.UNEXPECTED_ERROR,
        unexpectedErrorString: OpenAttestationDnsTxtCode[OpenAttestationDnsTxtCode.UNEXPECTED_ERROR],
    }),
};
